import { useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAdminUsersStore } from '../../store/adminUsersStore';
import type { UserRole } from '../../types';
import { WorksForSelect } from './WorksForSelect';

const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  INSIDE_SALES: 'Inside Sales',
  SALES_REP: 'Sales Rep',
  MATCHING_SUPPORT: 'Matching Support',
  DATA_STEWARD: 'Data Steward',
  CAMPAIGN_MANAGER: 'Campaign Manager',
  ASSISTANT: 'Assistant',
};

interface Props {
  onClose: () => void;
}

export function AddUserModal({ onClose }: Props) {
  const { createUser } = useAdminUsersStore();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roles, setRoles] = useState<Set<UserRole>>(new Set<UserRole>(['SALES_REP']));
  const [worksForUserId, setWorksForUserId] = useState('');
  const [saving, setSaving] = useState(false);

  const isAssistant = roles.has('ASSISTANT');
  const missingRep = isAssistant && !worksForUserId;

  function toggle(r: UserRole) {
    // Assistant is exclusive: checking it clears every other role.
    if (r === 'ASSISTANT') {
      if (roles.has('ASSISTANT')) {
        setRoles(new Set<UserRole>());
        setWorksForUserId('');
      } else {
        setRoles(new Set<UserRole>(['ASSISTANT']));
      }
      return;
    }
    const next = new Set(roles);
    if (next.has(r)) next.delete(r);
    else next.add(r);
    setRoles(next);
  }

  async function submit() {
    if (!email || roles.size === 0) {
      toast.error('Email and at least one role are required');
      return;
    }
    if (missingRep) {
      toast.error('Choose the sales rep this assistant works for');
      return;
    }
    setSaving(true);
    try {
      await createUser({
        email,
        displayName: displayName || undefined,
        roles: Array.from(roles),
        ...(isAssistant ? { worksForUserId } : {}),
      });
      toast.success('User pre-created. They will be activated on first SSO login.');
      onClose();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message ?? 'Failed to create user');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Add user</h2>
          <button onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Email *</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
            />
            <p className="mt-1 text-xs text-gray-500">
              Must match the user's Blast SSO email exactly. The CRM user becomes active on first
              SSO login.
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Display name</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Roles *</label>
            <div className="mt-1 space-y-1">
              {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={roles.has(r)}
                    disabled={r === 'ASSISTANT' ? roles.size > 0 && !isAssistant : isAssistant}
                    onChange={() => toggle(r)}
                  />
                  {ROLE_LABELS[r]}
                </label>
              ))}
            </div>
            {!isAssistant && roles.size > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Assistant can't be combined with other roles. Uncheck them to enable it.
              </p>
            )}
          </div>
          {isAssistant && (
            <WorksForSelect
              id="add-user-works-for"
              value={worksForUserId}
              onChange={setWorksForUserId}
            />
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || missingRep}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Create user'}
          </button>
        </div>
      </div>
    </div>
  );
}
